"use client";

import React, { useEffect, useState } from "react";
import { FormControl, TextField, Typography } from "@mui/material";
import { FormModeType } from "@/components/FormDataProvider";
import { useValidation } from "@/components/ValidationProvider";
import { Control } from "../ControlRenderer";
import ControlIcons from "../ControlIcons";
import { get, set } from "../utils/Utils";

interface ColorControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
}

const fallbackColor = "#000000";
const hexColorPattern = /^#[0-9a-fA-F]{6}$/;

const normalizeColorValue = (value: unknown): string => {
  return typeof value === "string" && hexColorPattern.test(value) ? value : fallbackColor;
};

export const ColorControl: React.FC<ColorControlProps> = ({ control, data, onDataChange, mode }) => {
  const { errors, warnings, validateData, setDataContext } = useValidation();
  const [selectedValue, setSelectedValue] = useState<string>(() =>
    normalizeColorValue(get(data, control.dataKey ?? "", fallbackColor))
  );

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;
  const warningMessage = warnings && control.dataKey ? warnings[control.dataKey] : undefined;
  const hasControlIcons = !!control.helpText || !!control.helpLink || !!errorMessage || !!warningMessage;
  const currentMode = control.mode ? control.mode : mode;

  useEffect(() => {
    setSelectedValue(normalizeColorValue(get(data, control.dataKey ?? "", fallbackColor)));
  }, [data, control.dataKey]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  const handleFieldChange = (fieldName: string, value: string) => {
    setSelectedValue(value);
    set(data, fieldName, value);
    onDataChange(data);
  };

  if (currentMode === "label") {
    return <Typography className="eform-labelmode">{selectedValue}</Typography>;
  }

  const colorInput = (
    <TextField
      variant="standard"
      type="color"
      fullWidth
      value={selectedValue}
      onBlur={validateData}
      onChange={(event) => handleFieldChange(control.dataKey ?? "", event.target.value)}
      inputProps={{
        readOnly: currentMode === "view",
        "aria-label": control.label || control.name || "Color",
      }}
      sx={{
        minWidth: control.minWidth,
        maxWidth: control.maxWidth,
        width: control.width,
        "& input": {
          cursor: currentMode === "view" ? "default" : "pointer",
          height: 32,
          padding: "2px 0",
        },
      }}
    />
  );

  return hasControlIcons ? (
    <FormControl variant="standard" fullWidth error={!!errorMessage}>
      {colorInput}
      <ControlIcons control={control} errorMessage={errorMessage} warningMessage={warningMessage} />
    </FormControl>
  ) : (
    colorInput
  );
};

export default ColorControl;
